import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { PageTitle } from "./shared";
import { SettingsPanel } from "./workshop-manager/SettingsPanel";
import { ShippingPanel } from "./workshop-manager/ShippingPanel";
import { BundlesPanel } from "./workshop-manager/BundlesPanel";
import { ShapesPanel, ColoursPanel } from "./workshop-manager/CataloguePanels";

const WorkshopManager = () => {
  const [settings, setSettings] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [colours, setColours] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [newShape, setNewShape] = useState({ key: "etchery_panel_stake", name: "" });
  const [newColour, setNewColour] = useState({ name: "", hex: "#EC2675" });
  const [newBundle, setNewBundle] = useState({ name: "", description: "", price: "", tag_count: 10 });

  useEffect(() => {
    Promise.all([
      api.get("/admin/workshop-settings"),
      api.get("/admin/shapes"),
      api.get("/admin/colours"),
      api.get("/admin/bundles"),
      api.get("/admin/inventory"),
    ]).then(([s, sh, co, bu, inv]) => {
      setSettings(s.data);
      setShapes(sh.data);
      setColours(co.data);
      setBundles(bu.data);
      setInventory(inv.data);
    }).catch(() => toast.error("Could not load workshop config"));
  }, []);

  const saveSettings = async () => {
    await api.put("/admin/workshop-settings", {
      ...settings,
      base_price: Number(settings.base_price),
      max_text_length: Number(settings.max_text_length),
      max_quantity: Number(settings.max_quantity),
      shipping_free_over: Number(settings.shipping_free_over || 0),
      stake_prices: Object.fromEntries(Object.entries(settings.stake_prices || {}).map(([k, v]) => [k, Number(v) || 0])),
      shipping_methods: (settings.shipping_methods || []).map((m) => ({ ...m, price: Number(m.price) || 0 })),
      quantity_discounts: (settings.quantity_discounts || [])
        .map((t) => ({ min_qty: Number(t.min_qty) || 0, percent: Number(t.percent) || 0 }))
        .filter((t) => t.min_qty > 0 && t.percent > 0),
    });
    toast.success("Workshop settings saved");
  };

  const patchShape = async (id, body) => {
    await api.patch(`/admin/shapes/${id}`, body);
    setShapes((l) => l.map((s) => (s.id === id ? { ...s, ...body } : s)));
  };
  const moveShape = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= shapes.length) return;
    const next = [...shapes];
    [next[index], next[target]] = [next[target], next[index]];
    setShapes(next);
    await api.post("/admin/shapes/reorder", { ids: next.map((s) => s.id) });
  };
  const addShape = async () => {
    if (!newShape.name.trim()) { toast.error("Give the shape a name"); return; }
    const { data } = await api.post("/admin/shapes", newShape);
    setShapes((l) => [...l, data]);
    setNewShape({ key: "etchery_panel_stake", name: "" });
    toast.success("Shape added");
  };
  const deleteShape = async (id) => {
    await api.delete(`/admin/shapes/${id}`);
    setShapes((l) => l.filter((s) => s.id !== id));
    toast.success("Shape removed");
  };

  const patchColour = async (id, body) => {
    await api.patch(`/admin/colours/${id}`, body);
    setColours((l) => l.map((c) => (c.id === id ? { ...c, ...body } : c)));
  };
  const addColour = async () => {
    if (!newColour.name.trim()) { toast.error("Give the colour a name"); return; }
    const { data } = await api.post("/admin/colours", newColour);
    setColours((l) => [...l, data]);
    setNewColour({ name: "", hex: "#EC2675" });
    toast.success("Colour added");
  };
  const deleteColour = async (id) => {
    await api.delete(`/admin/colours/${id}`);
    setColours((l) => l.filter((c) => c.id !== id));
    toast.success("Colour removed");
  };

  const patchBundle = async (id, body) => {
    await api.patch(`/admin/bundles/${id}`, body);
    setBundles((l) => l.map((b) => (b.id === id ? { ...b, ...body } : b)));
  };
  const addBundle = async () => {
    if (!newBundle.name.trim() || !Number(newBundle.price)) { toast.error("Bundle needs a name and price"); return; }
    const { data } = await api.post("/admin/bundles", {
      ...newBundle, price: Number(newBundle.price), tag_count: Number(newBundle.tag_count) || 10,
    });
    setBundles((l) => [...l, data]);
    setNewBundle({ name: "", description: "", price: "", tag_count: 10 });
    toast.success("Bundle added — it's now live in the workshop");
  };
  const deleteBundle = async (id) => {
    await api.delete(`/admin/bundles/${id}`);
    setBundles((l) => l.filter((b) => b.id !== id));
    toast.success("Bundle removed");
  };

  if (!settings) return <p className="text-plum-deep/60">Loading…</p>;

  return (
    <div data-testid="console-workshop-manager">
      <PageTitle kicker="PRODUCTS" title="WORKSHOP MANAGER" />
      <SettingsPanel settings={settings} setSettings={setSettings} onSave={saveSettings} />
      <ShippingPanel settings={settings} setSettings={setSettings} />
      <BundlesPanel
        bundles={bundles} setBundles={setBundles}
        newBundle={newBundle} setNewBundle={setNewBundle}
        onPatch={patchBundle} onAdd={addBundle} onDelete={deleteBundle}
      />
      <ShapesPanel
        shapes={shapes} setShapes={setShapes} inventory={inventory}
        newShape={newShape} setNewShape={setNewShape}
        onPatch={patchShape} onMove={moveShape} onAdd={addShape} onDelete={deleteShape}
      />
      <ColoursPanel
        colours={colours} setColours={setColours}
        newColour={newColour} setNewColour={setNewColour}
        onPatch={patchColour} onAdd={addColour} onDelete={deleteColour}
      />
    </div>
  );
};

export default WorkshopManager;
